/**
 * Internal Bulk Section-Moderator Grant/Revoke Sweep
 *
 * POST /internal/sections/moderator-grants/bulk  (checkApiKeyMiddleware —
 * internal, not a public `/v1/*` route)
 *
 * Deliberate short-term workaround: the "members may post" toggle
 * (`Section.memberPostingEnabled` / `UserGroup.memberPostingEnabled`) has no
 * reader on the content-write path — `canPostToSectionGroup` never reads the
 * `member-setting` tier (see `services/authorization/write-path-admitted-tiers.ts`).
 * The only non-admin tier the section write path DOES read is
 * `moderator-grant`, so this endpoint bulk-grants (or, for the exact reverse,
 * bulk-revokes) a `SpaceModeratorGrant` to every active member of a set of
 * sections. Every row it writes is stamped with the `SYSTEM_BULK_MODERATOR_SWEEP`
 * sentinel `grantedBy`, so the sweep is exactly reversible via `grant: false`
 * without disturbing a hand-granted "room parent" moderator (a different
 * `grantedBy`).
 */

import { z } from 'zod'

export const InternalBulkSectionModeratorGrantRequestSchema = z
  .object({
    school_id: z.string().min(1).describe('School public ID (CUID) or slug'),
    section_ids: z
      .array(z.string().min(1))
      .optional()
      .describe('Section PUBLIC ids to sweep, scoped to school_id. Mutually exclusive with offering_type.'),
    offering_type: z
      .string()
      .min(1)
      .optional()
      .describe(
        'Sweep every section at the school with this offering_type (e.g. COMMUNITY_GROUPS). Mutually exclusive with section_ids.'
      ),
    grant: z.boolean().describe('true = sweep grants on for every active member; false = revoke the sweep'),
    dry_run: z
      .boolean()
      .optional()
      .prefault(true)
      .describe('Defaults to true. A dry run performs ZERO writes and ZERO Stream calls, returning the planned counts.')
  })
  .refine(input => Boolean(input.section_ids?.length) !== Boolean(input.offering_type), {
    message: 'Exactly one of section_ids or offering_type is required',
    path: ['section_ids']
  })
export type InternalBulkSectionModeratorGrantRequest = z.infer<typeof InternalBulkSectionModeratorGrantRequestSchema>

/**
 * Per-section plan/result, shared shape for both a dry run and a live sweep.
 *
 * `status`/`error` (added post-review): each section is processed
 * independently, so one section's failure never discards the sections
 * already processed before it — see `failedSections` on the response. A
 * `status: 'failed'` section carries zeroed counts and `error` names the
 * failure; retry the whole request (idempotent) or investigate that section.
 */
export const InternalBulkSectionModeratorGrantSectionResultSchema = z.object({
  sectionPublicId: z.string(),
  name: z.string().nullable(),
  offeringType: z.string().nullable(),
  /**
   * The candidate set this section's counts are computed over. The meaning
   * DIFFERS by direction (post-review correction): for a GRANT sweep this is
   * the section's currently-ACTIVE, non-removed members. For a REVOKE sweep
   * this is every user who currently holds an ACTIVE `SpaceModeratorGrant` on
   * this section stamped `SYSTEM_BULK_MODERATOR_SWEEP` — which can include a
   * user who has since left the section (revoke must reach them too, or the
   * sweep would not be exactly reversible).
   */
  membersConsidered: z.number().int(),
  grantsCreated: z.number().int(),
  grantsRevived: z.number().int(),
  grantsRevoked: z.number().int(),
  legacyColumnRowsUpdated: z.number().int(),
  streamChannelsTouched: z.number().int(),
  streamOk: z.boolean(),
  status: z.enum(['ok', 'failed']),
  error: z.string().optional().describe('Present only when status is failed — the reason this section was skipped.')
})
export type InternalBulkSectionModeratorGrantSectionResult = z.infer<
  typeof InternalBulkSectionModeratorGrantSectionResultSchema
>

export const InternalBulkSectionModeratorGrantResponseSchema = z.object({
  dryRun: z.boolean(),
  grant: z.boolean(),
  school: z.object({
    publicId: z.string(),
    name: z.string().nullable()
  }),
  /** Sums ONLY the `status: 'ok'` sections — a failed section contributes zero. */
  totals: z.object({
    sectionsMatched: z.number().int(),
    membersConsidered: z.number().int(),
    grantsCreated: z.number().int(),
    grantsRevived: z.number().int(),
    grantsRevoked: z.number().int(),
    legacyColumnRowsUpdated: z.number().int(),
    streamChannelsTouched: z.number().int(),
    streamOk: z.boolean()
  }),
  /** How many of `sections` came back `status: 'failed'`. 0 means every matched section processed cleanly. */
  failedSections: z.number().int(),
  /** section_ids the caller supplied that did not match a section at school_id (wrong school, or unknown). */
  unmatchedSectionIds: z.array(z.string()),
  sections: z.array(InternalBulkSectionModeratorGrantSectionResultSchema)
})
export type InternalBulkSectionModeratorGrantResponse = z.infer<typeof InternalBulkSectionModeratorGrantResponseSchema>
